export interface HealthProviderAddress {
  line1: string;
  line2?: string;
  city: string;
  state: string;
  zip: string;
  country?: string;
}

export interface HealthPhoto {
  id: string;
  url: string;
  storagePath?: string;
  caption?: string;
  type?: string;
  uploadedAt: string;
  // Multi-provider storage support
  provider?: import('./storage').StorageProvider;
  googleDriveFileId?: string;
  pendingFile?: File; // Deferred upload — file object not yet uploaded to storage
}

export interface HealthDocument {
  id: string;
  name: string;
  url: string;
  storagePath?: string;
  type: 'insurance_card' | 'prescription' | 'lab_result' | 'medical_record' | 'immunization' | 'invoice' | 'referral' | 'other';
  size?: number;
  uploadedAt: string;
  effectiveDate?: string; // ISO date — start of validity period
  expiresAt?: string;
  // Multi-provider storage support
  provider?: import('./storage').StorageProvider;
  googleDriveFileId?: string;
  pendingFile?: File; // Deferred upload — file object not yet uploaded to storage
}

// Healthcare category types (kept for backward compat — old records have this field)
export type HealthcareCategory = 'care_provider' | 'pharmacy' | 'lab_diagnostics' | 'hospital_facility';

// Legacy care provider types (kept for backward compat — old records may have these values)
export type CareProviderType = 'primary_care' | 'specialist' | 'dentist' | 'vision' | 'mental_health' | 'surgeon' | 'other';

// ── Unified Provider Type (flat list — replaces Category + CareProviderType + Specialty) ──

export type ProviderType =
  | 'doctor' | 'specialist' | 'dentist' | 'vision' | 'mental_health' | 'surgeon'
  | 'pharmacy' | 'hospital' | 'urgent_care' | 'lab' | 'other';

export interface ProviderTypeMeta {
  label: string;
  category: HealthcareCategory;
  color: 'blue';
}

export const PROVIDER_TYPE_META: Record<ProviderType, ProviderTypeMeta> = {
  doctor:        { label: 'Doctor',          category: 'care_provider',      color: 'blue' },
  specialist:    { label: 'Specialist',      category: 'care_provider',      color: 'blue' },
  dentist:       { label: 'Dentist',         category: 'care_provider',      color: 'blue' },
  vision:        { label: 'Vision',          category: 'care_provider',      color: 'blue' },
  mental_health: { label: 'Mental Health',   category: 'care_provider',      color: 'blue' },
  surgeon:       { label: 'Surgeon',         category: 'care_provider',      color: 'blue' },
  pharmacy:      { label: 'Pharmacy',        category: 'pharmacy',           color: 'blue' },
  hospital:      { label: 'Hospital',        category: 'hospital_facility',  color: 'blue' },
  urgent_care:   { label: 'Urgent Care',     category: 'hospital_facility',  color: 'blue' },
  lab:           { label: 'Lab',             category: 'lab_diagnostics',    color: 'blue' },
  other:         { label: 'Other',           category: 'care_provider',      color: 'blue' },
};

/**
 * Resolve provider type from a HealthProvider — handles old records that only have `category`.
 * Old `CareProviderType` values are mapped to the new `ProviderType` (primary_care → doctor).
 */
export function resolveProviderType(provider: Pick<HealthProvider, 'providerType' | 'category'>): ProviderType {
  const pt = provider.providerType;
  // New-format record: providerType is already a ProviderType value
  if (pt && pt in PROVIDER_TYPE_META) return pt as ProviderType;
  // Old care_provider record with legacy CareProviderType
  if (pt === 'primary_care') return 'doctor';
  // Derive from category for old records without providerType
  const categoryMap: Record<string, ProviderType> = {
    care_provider: 'doctor',
    pharmacy: 'pharmacy',
    lab_diagnostics: 'lab',
    hospital_facility: 'hospital',
  };
  return categoryMap[provider.category] || 'other';
}

/**
 * AssignedTo - Specifies which family members an entity is assigned to
 * - 'family': Applies to all family members (default)
 * - string[]: Array of specific member userIds
 */
export type AssignedTo = 'family' | string[];

export interface HealthProvider {
  id: string;
  name: string;
  category: HealthcareCategory; // Main category (care provider, pharmacy, lab, hospital)
  providerType?: ProviderType | CareProviderType; // Unified type (new: ProviderType, old: CareProviderType)
  specialty?: string; // Free text specialty (e.g., "Cardiologist", "Family Medicine")
  phone?: string;
  email?: string;
  address?: HealthProviderAddress;
  notes?: string;
  isPrimary?: boolean;
  isStarred?: boolean;
  isFavorite?: boolean; // Alias for isStarred for consistency
  photos?: HealthPhoto[];
  documents?: HealthDocument[];
  // Google Places data for search results
  placeId?: string;
  rating?: number;
  reviewsCount?: number;
  hours?: string;
  formatted_address?: string;
  latitude?: number;
  longitude?: number;
  isOpen?: boolean;
  website?: string; // Provider website URL
  // Portals - payment portals, patient portals, etc.
  portals?: import('./portal').Portal[];
  dismissedPortalUrls?: string[]; // URLs that user has dismissed from AI suggestions
  createdAt: string;
  updatedAt?: string;
  assignedTo?: AssignedTo; // Which family members this provider is assigned to (defaults to 'family' = all)
  isPrivate?: boolean; // If true, only the owner can see this item (hidden from Editors, Viewers, AI Search, Share Bundles)
}
